import { StoreError } from '@/types/storeError.interface';
import {
  createStoreSlice as createSlice,
  type PayloadAction,
} from '@/store/core';
import type { IndexedSignedMissingItemFormPdfs } from '@/typings';
import { RootState } from '@/store/store';

interface MissingItemSignedPdfState {
  loading: boolean;
  error: StoreError | null;
  signedMissingItemFormsPdfs: IndexedSignedMissingItemFormPdfs;
  loadingById: Record<number, boolean>;
  /**
   * Marks that we've already resolved this missing item form id for the
   * signed-PDF preview (success, not-found, or other failure).
   * This prevents repeated refetch loops when a PDF isn't available yet.
   */
  resolvedById: Record<number, boolean>;
}

const initialState: MissingItemSignedPdfState = {
  loading: false,
  error: null,
  signedMissingItemFormsPdfs: {},
  loadingById: {},
  resolvedById: {},
};

export const missingItemSignedPdfSlice = createSlice({
  name: 'missingItemSignedPdf',
  initialState,
  reducers: {
    missingItemSignedPdfLoading: state => {
      state.loading = true;
    },
    fetchSignedMissingItemFormPdfStart: (
      state,
      action: PayloadAction<{ missingItemFormId: number }>,
    ) => {
      state.loading = true;
      state.error = null;
      state.loadingById[action.payload.missingItemFormId] = true;
      state.resolvedById[action.payload.missingItemFormId] = false;
    },
    fetchSignedMissingItemFormPdfSuccess: (
      state,
      action: PayloadAction<{
        missingItemFormId: number;
        pdf: IndexedSignedMissingItemFormPdfs[number];
      }>,
    ) => {
      state.loading = false;
      state.error = null;
      state.signedMissingItemFormsPdfs[action.payload.missingItemFormId] =
        action.payload.pdf;
      state.loadingById[action.payload.missingItemFormId] = false;
      state.resolvedById[action.payload.missingItemFormId] = true;
    },
    fetchSignedMissingItemFormPdfFailure: (
      state,
      action: PayloadAction<{
        missingItemFormId: number;
        error: StoreError;
      }>,
    ) => {
      state.loading = false;
      state.error = action.payload.error;
      state.loadingById[action.payload.missingItemFormId] = false;
      state.resolvedById[action.payload.missingItemFormId] = true;
    },
    fetchSignedMissingItemFormPdfNotFound: (
      state,
      action: PayloadAction<{ missingItemFormId: number }>,
    ) => {
      // 404 / BUS_NOT_FOUND is a non-fatal state for the POC:
      // the UI can show the template preview or a placeholder.
      state.loadingById[action.payload.missingItemFormId] = false;
      state.resolvedById[action.payload.missingItemFormId] = true;
    },
    clearSignedMissingItemFormPdf: state => {
      state.signedMissingItemFormsPdfs = {};
      state.loading = false;
      state.error = null;
      state.loadingById = {};
      state.resolvedById = {};
    },
  },
});

export const {
  missingItemSignedPdfLoading,
  fetchSignedMissingItemFormPdfStart,
  fetchSignedMissingItemFormPdfSuccess,
  fetchSignedMissingItemFormPdfFailure,
  fetchSignedMissingItemFormPdfNotFound,
  clearSignedMissingItemFormPdf,
} = missingItemSignedPdfSlice.actions;

export const selectMissingItemSignedPdf = (state: RootState) =>
  state[missingItemSignedPdfSlice.name] ?? initialState;

